import { c } from '../../intl'
import type { ApiRound } from '../../round/models/ApiRound'
import type { Round } from '../../round/models/Round'
import {
  createFieldChoice,
  createFieldImageChoice,
  createFieldNa,
  createFieldNone,
  createFieldNps,
  createFieldOther,
  createFieldRanked,
  createFieldSkip,
  createFieldText,
  createFieldVideo,
} from '../factories/Field'
import {
  createQuestionChoice,
  createQuestionGaborGranger,
  createQuestionImageChoice,
  createQuestionNps,
  createQuestionOpenText,
  createQuestionRanked,
  createQuestionVideo,
} from '../factories/Question'
import type { ApiQuestion, ApiQuestionAnswer } from '../models/ApiQuestion'
import type { FieldSpecial } from '../models/Field'
import type { Question } from '../models/Question'

export const transformApiQuestion = (round: Round, apiQuestion: ApiQuestion): Question[] => {
  switch (apiQuestion.type) {
    case 'text_response_long':
    case 'text_response_medium':
    case 'text_response_short': {
      return [transformApiOpenTextQuestion(round, apiQuestion)]
    }
    case 'video': {
      return [transformApiVideoQuestion(round, apiQuestion)]
    }
    case 'nps': {
      return [transformApiNpsQuestion(round, apiQuestion)]
    }
    case 'single_choice':
    case 'multiple_choice': {
      return [transformApiChoiceQuestion(round, apiQuestion)]
    }
    case 'ranked': {
      return [transformApiRankedQuestion(round, apiQuestion)]
    }
    case 'grid': {
      return transformApiGridQuestion(round, apiQuestion)
    }
    case 'max_diff': {
      return transformApiMaxDiffQuestion(apiQuestion)
    }
    case 'gabor_granger': {
      return transformApiGaborGrangerQuestion(apiQuestion)
    }
    case 'slider': {
      return [transformApiSliderQuestion(apiQuestion)]
    }
  }
}

const transformApiQuestionCommon = (
  apiQuestion: Pick<ApiQuestion, 'id' | 'title' | 'title-content'>,
): Pick<Question, 'id' | 'title' | 'media'> => {
  return {
    id: apiQuestion.id,
    title: apiQuestion.title,
    ...(() => {
      const apiTitleContent = apiQuestion['title-content']
      const muxMedia = getMuxMediaFromTitleContent(apiTitleContent)
      if (muxMedia) {
        return { media: muxMedia }
      }

      const type = (['audio', 'image', 'video'] as const).find((mediaType) =>
        Boolean(apiTitleContent[`${mediaType}-url`]),
      )
      if (!type) {
        return
      }
      const url = apiTitleContent[`${type}-url`]
      if (!url) {
        throw new Error('expect url to be defined')
      }
      return { media: { url, type } }
    })(),
  }
}

const getMuxMediaFromTitleContent = (
  titleContent: Pick<ApiQuestion, 'title-content'>['title-content'],
): Pick<Question, 'media'>['media'] | undefined => {
  const mux = titleContent.mux
  if (!mux) {
    return undefined
  }

  return {
    playbackId: mux['playback-id'],
    playbackToken: mux['playback-token'] ?? '',
    thumbnailToken: mux['thumbnail-token'] ?? '',
    type: 'mux',
  }
}

export const OPEN_QUESTION_TEXT_CHARACTER_LIMITS = {
  text_response_short: 60,
  text_response_medium: 140,
  text_response_long: 800,
} as const

const transformApiOpenTextQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<`text_response_${'short' | 'medium' | 'long'}`>,
): Question<'openText'> =>
  createQuestionOpenText({
    ...transformApiQuestionCommon(apiQuestion),
    fields: [
      ...apiQuestion.answers.map((answer) =>
        createFieldText({ id: answer.id, title: answer.text ?? '' }),
      ),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
    characterLimit: OPEN_QUESTION_TEXT_CHARACTER_LIMITS[apiQuestion.type],
  })

const transformApiVideoQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'video'>,
): Question<'video'> => {
  const [videoAnswer] = apiQuestion.answers
  const [objectStorage] = apiQuestion['object-storage']
  if (videoAnswer?.type !== 'text') {
    throw new Error('expect video question to have text answer')
  }
  if (!objectStorage?.url || !objectStorage?.id) {
    throw new Error('expected object storage to have upload url and video id')
  }

  return createQuestionVideo({
    ...transformApiQuestionCommon(apiQuestion),
    prompt: videoAnswer.text ?? '',
    fields: [
      createFieldVideo({
        id: videoAnswer.id,
      }),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
  })
}

const transformApiNpsQuestion = (round: Round, apiQuestion: ApiQuestion<'nps'>): Question<'nps'> =>
  createQuestionNps({
    ...transformApiQuestionCommon(apiQuestion),
    fields: [
      createFieldNps({
        id: 'nps',
        ratingToApiAnswerIdMap: new Map(apiQuestion.answers.map((answer, i) => [i, answer.id])),
      }),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
  })

const transformApiChoiceQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'single_choice' | 'multiple_choice'>,
): Question<'choice'> | Question<'imageChoice'> => {
  const partialChoiceQuestion: Omit<Question<'choice' | 'imageChoice'>, 'fields' | 'type'> = {
    ...transformApiQuestionCommon(apiQuestion),
    maxAnswers:
      apiQuestion.type === 'single_choice'
        ? 1
        : (apiQuestion.max_selections ?? Number.POSITIVE_INFINITY),
  }

  return {
    text: () =>
      createQuestionChoice({
        ...partialChoiceQuestion,
        fields: [
          ...apiQuestion.answers.map((answer) =>
            createFieldChoice({ id: answer.id, title: answer.text ?? '' }),
          ),
          ...(apiQuestion['has-other'] ? [createFieldOther()] : []),
          ...createSpecialFields({
            ...apiQuestion,
            'skip-disabled': round.skippedDisabled,
          }),
        ],
        subject: apiQuestion['subject-title'],
      }),
    image: () =>
      createQuestionImageChoice({
        ...partialChoiceQuestion,
        fields: [
          ...apiQuestion.answers.map((answer) => {
            if (answer.type !== 'image') {
              throw new Error('expect all api answers are of type image')
            }
            return createFieldImageChoice({
              id: answer.id,
              url: answer.url,
              description: answer.text,
            })
          }),
          ...createSpecialFields({
            ...apiQuestion,
            'skip-disabled': round.skippedDisabled,
          }),
        ],
        display: apiQuestion['image-answer-display-size'],
        subject: apiQuestion['subject-title'],
      }),
  }[getChoiceQuestionType(apiQuestion)]()
}

const transformApiRankedQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'ranked'>,
): Question<'ranked'> =>
  createQuestionRanked({
    ...transformApiQuestionCommon(apiQuestion),
    fields: [
      ...apiQuestion.answers.map((answer) =>
        createFieldRanked({ id: answer.id, title: answer.text ?? '' }),
      ),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
  })

const transformApiGridQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'grid'>,
): Question<'choice' | 'imageChoice'>[] => {
  return apiQuestion.answers.subjects.headers.map((subjectHeader, subjectIndex) => {
    const apiChoiceQuestionPartial = {
      'has-na': apiQuestion.answers.options.na,
      'has-none': apiQuestion.answers.options.none,
      'has-other': apiQuestion['has-other'],
      'image-answer-display-size': apiQuestion['image-answer-display-size'],
      'is-randomized': apiQuestion['is-randomized'],
      'title-content': apiQuestion['title-content'],
      id: apiQuestion.id,
      title: apiQuestion.title,
      answers: apiQuestion.answers.options.headers.map(
        (optionHeader, optionIndex): ApiQuestionAnswer => {
          const answerId = apiQuestion.answers.choices[subjectIndex]?.values[optionIndex]?.id
          if (!answerId) {
            throw new Error(
              `Could not find field id for option '${optionHeader.text}' in ${apiQuestion.answers.choices[subjectIndex]}`,
            )
          }

          if (optionHeader.type === 'text') {
            return {
              id: answerId,
              type: optionHeader.type,
              text: optionHeader.text,
            }
          }
          if (optionHeader.type === 'image') {
            return {
              id: answerId,
              type: optionHeader.type,
              text: optionHeader.text,
              url: optionHeader.url,
            }
          }

          throw new Error(`unrecognised optionHeader type`)
        },
      ),
    }

    return {
      ...transformApiChoiceQuestion(
        round,
        {
          single_choice: {
            ...apiChoiceQuestionPartial,
            type: 'single_choice' as const,
          },
          multiple_choice: {
            ...apiChoiceQuestionPartial,
            type: 'multiple_choice' as const,
            max_selections: apiQuestion.answers.options.max_selections,
          },
        }[apiQuestion.answers.options.type],
      ),
      subject: subjectHeader.text,
      ...(subjectHeader.type === 'image' ? { subjectImage: subjectHeader.url } : {}),
    }
  })
}

/**
 * Applied when the payload carries no `scale`. Defaulting here rather than in the component keeps
 * the scale a property of the question, so a custom range only has to start arriving on the wire.
 */
export const DEFAULT_SLIDER_SCALE = { min: 1, max: 100, step: 1 }

const transformApiSliderQuestion = (apiQuestion: ApiQuestion<'slider'>): Question<'slider'> => ({
  ...transformApiQuestionCommon(apiQuestion),
  type: 'slider' as const,
  // A slider has no choices, so the single field exists purely to hang the answer off -- there is
  // nothing in apiQuestion.answers to map. Every other question type derives its fields from them.
  fields: [{ id: 'slider', type: 'slider' as const }],
  startLabel: apiQuestion.labels.start,
  endLabel: apiQuestion.labels.end,
  // Absent on older API responses -- default to off for backward compatibility.
  showValues: apiQuestion['show-values'] ?? false,
  ...(apiQuestion.scale ?? DEFAULT_SLIDER_SCALE),
})

const transformApiMaxDiffQuestion = (
  apiQuestion: ApiQuestion<'max_diff'>,
): Question<'maxDiffSet'>[] => {
  return Object.entries(apiQuestion.answers).map(([setId, set], setIndex, sets) => ({
    ...transformApiQuestionCommon(apiQuestion),
    type: 'maxDiffSet' as const,
    fields: set.map((answer) => ({
      id: `${setId}-${answer.id}`,
      setId,
      answerId: answer.id,
      type: 'maxDiffSetChoice',
      title: answer.text,
    })),
    bestLabel: apiQuestion.labels.best,
    worstLabel: apiQuestion.labels.worst,
    subject: c('max_diff.set_subtitle', {
      placeholders: {
        setNumber: setIndex + 1,
        numberOfSets: sets.length,
      },
    }),
  }))
}

// A plain grid payload is a valid grid, so without this check a missing gabor granger field
// renders a blank card instead of reporting anything.
const assertApiGaborGrangerContract = (apiQuestion: ApiQuestion<'gabor_granger'>): void => {
  const { subjects, options, choices } = apiQuestion.answers
  const missing = [
    // every() is true for an empty array, so check the lengths before the per-entry rules below.
    ...(subjects.headers.length > 0 ? [] : ['at least one price point']),
    ...(options.headers.length > 0 ? [] : ['at least one scale option']),
    ...(apiQuestion.currency ? [] : ['currency on the question']),
    ...(subjects.headers.every((header) => header.id)
      ? []
      : ['id on every subjects.headers entry']),
    ...(subjects.headers.every((header) => typeof header.value === 'number')
      ? []
      : ['numeric value on every subjects.headers entry']),
    ...(options.headers.every((header) => header.id) ? [] : ['id on every options.headers entry']),
    ...(options.headers.every((header) => typeof header.score === 'number')
      ? []
      : ['numeric score on every options.headers entry']),
    ...(choices.every(({ values }) => values.every((cell) => cell.subject_id && cell.option_id))
      ? []
      : ['subject_id and option_id on every choices cell']),
  ]

  if (missing.length > 0) {
    throw new Error(
      `gabor granger question ${apiQuestion.id} is missing ${missing.join(', ')}. ` +
        'The card looks like a plain grid payload.',
    )
  }
}

const transformApiGaborGrangerQuestion = (
  apiQuestion: ApiQuestion<'gabor_granger'>,
): Question<'gaborGranger'>[] => {
  assertApiGaborGrangerContract(apiQuestion)

  // Sorted here rather than trusted from the payload, because the staircase steps between
  // neighbouring prices and a wrong order would send it the wrong way with nothing to show it.
  const pricePoints = [...apiQuestion.answers.subjects.headers].toSorted(
    (a, b) => a.value - b.value,
  )
  const cellIdsBySubjectAndOption = new Map(
    apiQuestion.answers.choices.flatMap(({ values }) =>
      values.map((cell) => [`${cell.subject_id}:${cell.option_id}`, cell.id] as const),
    ),
  )

  return pricePoints.map((pricePoint) =>
    createQuestionGaborGranger({
      ...transformApiQuestionCommon(apiQuestion),
      price: { id: pricePoint.id, value: pricePoint.value, currency: apiQuestion.currency },
      fields: apiQuestion.answers.options.headers.map((option) => {
        const cellId = cellIdsBySubjectAndOption.get(`${pricePoint.id}:${option.id}`)
        if (!cellId) {
          throw new Error(
            `expect a cell for gabor granger scale option '${option.text}' at price ${pricePoint.value}`,
          )
        }
        return createFieldChoice({ id: cellId, title: option.text })
      }),
    }),
  )
}

export const createSpecialFields = (
  options: Pick<ApiQuestion, 'has-na' | 'has-none'> & Pick<ApiRound, 'skip-disabled'>,
): FieldSpecial[] => {
  return [
    ...(options['has-none'] ? [createFieldNone()] : []),
    ...(options['has-na'] ? [createFieldNa()] : []),
    ...(options['skip-disabled'] ? [] : [createFieldSkip()]),
  ]
}

const getChoiceQuestionType = (
  apiQuestion: ApiQuestion<'single_choice' | 'multiple_choice'>,
): 'text' | 'image' => {
  if (apiQuestion.answers.every(({ type }) => type === 'text')) {
    return 'text'
  }
  if (apiQuestion.answers.every(({ type }) => type === 'image')) {
    return 'image'
  }
  throw new Error('do not expect a mixture of image and text answers')
}
